import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { i18n } from 'i18next';

import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type {
  ProcessFlow,
  ProcessFlowNodeEnd,
  ProcessFlowNodeStep,
  ProcessFlowStep,
  ProcessFlowStepAction,
  ProcessFlowStepBusinessRule,
  ProcessFlowStepCompletion,
  ProcessFlowStepCondition,
  ProcessFlowStepConnector,
  ProcessFlowStepTask,
  ProcessFlowStepTaskRoutingCloud,
  ProcessFlowStepTaskRoutingDocuware,
  RoutingFile,
  RoutingFileCsv,
  RoutingFileJson,
  RoutingFileTxt,
  RoutingFileXml,
} from '~/common/model/api/batch-type/process-flow';

import type { ProcessFlowStepData, ProcessFlowStepDataStep } from './_step.type';

import { getNode } from '../_node';

/** Is step. */
export const isStep = {
  task: <TTask extends ProcessFlowStepTask>(step: Nullable<ProcessFlowStep>, type: TTask['settings']['$is']): step is TTask =>
    isType<TTask>(step, 'task') && isType<TTask['settings']>(step.settings, type),

  businessRules: <TBusinessRule extends ProcessFlowStepBusinessRule>(
    step: Nullable<ProcessFlowStep>,
    type: TBusinessRule['settings']['$is'],
  ): step is TBusinessRule => isType<TBusinessRule>(step, 'businessRule') && isType<TBusinessRule['settings']>(step.settings, type),

  condition: <TCondition extends ProcessFlowStepCondition>(step: Nullable<ProcessFlowStep>, type: TCondition['settings']['$is']): step is TCondition =>
    isType<TCondition>(step, 'condition') && isType<TCondition['settings']>(step.settings, type),

  connector: <TConnector extends ProcessFlowStepConnector>(step: Nullable<ProcessFlowStep>, type: TConnector['settings']['$is']): step is TConnector =>
    isType<TConnector>(step, 'connector') && isType<TConnector['settings']>(step.settings, type),

  action: <TAction extends ProcessFlowStepAction>(step: Nullable<ProcessFlowStep>, type: TAction['settings']['$is']): step is TAction =>
    isType<TAction>(step, 'action') && isType<TAction['settings']>(step.settings, type),
};

/**
 * Get ProcessFlow Step.
 *
 * @param {ProcessFlow} data ProcessFlow.
 * @param {ProcessFlowNodeStep | string} node Node or node id.
 *
 * @returns {ProcessFlowStepData | undefined} ProcessFlow Step result.
 */
export function getStep(data: ProcessFlow, node: ProcessFlowNodeStep | ProcessFlowNodeEnd | string): ProcessFlowStepData | undefined {
  const targetNode = typeof node === 'string' ? getNode(data.layout, node) : node;
  if (!targetNode) {
    return undefined;
  }

  const step = data.steps.find(({ attributes }) => attributes.key === targetNode.id);
  if (!step) {
    return undefined;
  }

  if (targetNode.type === 'end' && isType<ProcessFlowStepCompletion>(step, 'completion')) {
    return {
      node: targetNode,
      step,
    };
  }

  if (targetNode.type === 'step' && !isType<ProcessFlowStepCompletion>(step, 'completion')) {
    return {
      node: targetNode,
      step,
    };
  }

  return undefined;
}

/**
 * Check if step is ProcessFlowNodeStep.
 *
 * @param {ProcessFlowStepData} step ProcessFlowStepData.
 *
 * @returns {boolean} Whether step is ProcessFlowNodeStep .
 */
function isStepOnlyData(step: ProcessFlowStepData): step is ProcessFlowStepDataStep {
  return step.node.type === 'step';
}

/**
 * Check if step is a routing task (cloud or DocuWare), i.e. a step whose settings hold RoutingFiles
 * backed by ExportTemplate/ExportTemplateDetail records.
 *
 * @param {ProcessFlowStepDataStep['step']} step Step.
 *
 * @returns {boolean} Whether step is a routing task.
 */
function isRoutingTaskStep(step: ProcessFlowStepDataStep['step']): step is ProcessFlowStepTaskRoutingCloud | ProcessFlowStepTaskRoutingDocuware {
  return isStep.task<ProcessFlowStepTaskRoutingCloud>(step, 'routing') || isStep.task<ProcessFlowStepTaskRoutingDocuware>(step, 'routing.docuware');
}

/**
 * Regenerate ids for a routing task's files and their export templates/template details.
 *
 * Every id here (RoutingFile, Template, TemplateDetail/value) is a real, persisted backend record id.
 * When duplicating a step, these must never be carried over as-is: the backend matches/creates
 * ExportTemplate and ExportTemplateDetail rows by these ids, so reusing them would make the duplicated
 * step's save collide with (and overwrite) the original step's export template data.
 *
 * @param {RoutingFile[]} files Original routing files.
 *
 * @returns {RoutingFile[]} Routing files with freshly generated ids.
 */
function duplicateRoutingFiles(files: RoutingFile[]): RoutingFile[] {
  return files.map((file) => {
    const newFile = { ...file, id: newKey() };

    if (isType<RoutingFileCsv>(newFile, 'csv') || isType<RoutingFileJson>(newFile, 'json') || isType<RoutingFileXml>(newFile, 'xml')) {
      return {
        ...newFile,
        template: {
          ...newFile.template,
          id: newKey(),
          fields: newFile.template.fields.map((field) => ({ ...field, id: newKey() })),
        },
      };
    }

    if (isType<RoutingFileTxt>(newFile, 'txt')) {
      return {
        ...newFile,
        template: {
          ...newFile.template,
          id: newKey(),
          values: newFile.template.values.map((value) => ({ ...value, id: newKey() })),
        },
      };
    }

    return newFile;
  });
}

/**
 * Create duplicate step data.
 *
 * @param {i18n['t']} t Translation function.
 * @param {ProcessFlowStepData} originalStep Original Step Data.
 *
 * @returns {ProcessFlowStepData | undefined} Duplicate Data.
 */
export function createDuplicateStep(t: i18n['t'], originalStep: ProcessFlowStepData): ProcessFlowStepData | undefined {
  if (!originalStep) {
    return undefined;
  }

  if (!isStepOnlyData(originalStep)) return undefined;

  const newNodeId = newKey();
  const originalStepData = originalStep.step;
  const duplicatedName = t('feature:settings.batchTypeLibrary.processFlow.form.duplicateName', {
    name: originalStepData.name,
  });
  const duplicatedAttributes = {
    ...originalStepData.attributes,
    key: newNodeId,
  };

  const newStepData: ProcessFlowStepDataStep['step'] = isRoutingTaskStep(originalStepData)
    ? ({
        ...originalStepData,
        id: newKey(),
        name: duplicatedName,
        attributes: duplicatedAttributes,
        settings: {
          ...originalStepData.settings,
          files: duplicateRoutingFiles(originalStepData.settings.files),
        },
      } as typeof originalStepData)
    : {
        ...originalStepData,
        id: newKey(),
        name: duplicatedName,
        attributes: duplicatedAttributes,
      };

  return {
    ...originalStep,
    node: {
      ...originalStep.node,
      id: newNodeId,
      data: {
        ...originalStep.node.data,
        label: t('feature:settings.batchTypeLibrary.processFlow.form.duplicateName', {
          name: originalStep.node.data.label,
        }),
      },
    },
    step: newStepData,
  };
}
